import * as Sentry from '@sentry/react'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { showErrorToast } from '@/src/renderer/components/common/MessageToast'
import { useTranslation } from 'react-i18next'
import {
  getCurrentUser,
  getCurrentSession,
  readStoredSession,
  loginUser,
  logoutUser,
  registerUser,
  onAuthStateChange,
  type LoginParams,
  type RegisterParams,
  type LoginResult,
  type RegisterResult,
  type LogoutResult,
} from '../lib/supabase/auth'
import type { AuthUser, AuthSession } from '../lib/supabase/types'
import { supabase } from '../lib/supabase/client'
import { wipeUserLocalState } from '../lib/auth/wipeLocalState'
import { clearUserDisplayProfileCache } from '../hooks/useUserDisplayProfile'
import { identifyUser, resetAnalytics } from '../lib/analytics'
import { isThrottleElapsed } from '../lib/sync/throttle'
import { withTimeout } from '../lib/withTimeout'

/**
 * Authentication Context Provider
 * Manages auth state and provides auth functions to the app
 */

/**
 * **Wie lange der Start höchstens aufs Netz wartet.**
 *
 * Fünf Sekunden: weit über jeder gesunden Antwort (~200 ms) und kurz genug,
 * dass niemand die App für tot hält.
 *
 * Anlass (17.09.2026): Die App zeigte beim Start nur das Logo. Sauber offline
 * war nie das Problem — dort scheitert `fetch` sofort. Es ist das halbe WLAN:
 * Die Verbindung steht, niemand antwortet, und ohne Frist wartete der
 * Logo-Schirm minutenlang.
 */
export const START_NETWORK_BUDGET = 5_000

/**
 * Unterscheidet „Frist abgelaufen" von „es gibt wirklich keine Sitzung".
 * Beides als `null` zu behandeln hieße, bei jedem Netzhänger die gespeicherte
 * Sitzung zu suchen, die es gar nicht gibt.
 */
const TIMED_OUT = Symbol('timed-out') as unknown as AuthSession

interface AuthContextValue {
  user: AuthUser | null
  session: AuthSession | null
  loading: boolean
  isAuthenticated: boolean

  // Auth actions
  login: (params: LoginParams) => Promise<LoginResult>
  register: (params: RegisterParams) => Promise<RegisterResult>
  logout: () => Promise<LogoutResult>
}

// Exported for components that render outside the provider tree (e.g. Titlebar)
// and need safe access via useContext() which returns undefined instead of throwing.
export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * **Der Sentry-Scope hängt am angemeldeten Nutzer, nicht am Wechsel.**
 *
 * Das Feedback-Formular füllt sein E-Mail-Feld daraus
 * (`useSentryUser` in `lib/sentry/index.ts`). Stand der Aufruf nur im
 * Auth-Listener, blieb das Feld nach jedem Neustart leer: Beim Start mit
 * gespeicherter Sitzung hat `initAuth` die ID schon gesetzt, das nachgemeldete
 * `INITIAL_SESSION` läuft damit in die Dedup-Sperre und erreicht ihn nie.
 *
 * Deshalb rufen ihn beide Wege — und über dieselbe Funktion, damit sie nicht
 * auseinanderlaufen. `Sentry.setUser` ist idempotent; ein zweiter Aufruf mit
 * denselben Werten kostet nichts und rendert nicht.
 */
function syncSentryUser(user: AuthUser | null): void {
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email })
  } else {
    Sentry.setUser(null)
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { t } = useTranslation()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<AuthSession | null>(null)
  const [loading, setLoading] = useState(true)

  // Track current user ID to skip redundant state updates.
  // Supabase fires onAuthStateChange multiple times on login (INITIAL_SESSION,
  // SIGNED_IN, TOKEN_REFRESHED) — each would trigger a full re-render cascade
  // through MainLayout → useTabManager, destabilizing active tab state.
  const userIdRef = useRef<string | null>(null)
  // Supabase re-fires onAuthStateChange (TOKEN_REFRESHED / SIGNED_IN) on every
  // window focus, and each carried the is_active profiles check below — observed
  // as `profiles ×10` on a single refocus. Throttle that network check to at most
  // once per window (still forced on a real user change). 0 = "never" → first runs.
  const lastActiveCheckAtRef = useRef(0)
  const ACTIVE_CHECK_THROTTLE_MS = 60_000

  // Note: Recovery token handling is done in App.tsx BEFORE router initialization
  // This prevents BrowserRouter from treating the hash as a route

  // Initialize auth state — session-first for offline resilience
  useEffect(() => {
    const initAuth = async () => {
      try {
        /*
          **Der Start wartet höchstens `START_NETWORK_BUDGET` aufs Netz.**

          `getSession()` liest zwar aus dem Speicher, erneuert aber ein
          abgelaufenes Token — und das geht ans Netz. Läuft die Frist ab, wird
          die Sitzung **ohne Netz** aus dem Speicher gelesen: Der Nutzer kommt
          herein und arbeitet lokal weiter, der Sync ruht und holt nach.

          Ohne diesen Rückfall wäre `isAuthenticated` falsch, und
          `ProtectedRoute` schickte einen angemeldeten Nutzer auf den
          Setup-Bildschirm — schlimmer als das Logo.
        */
        let currentSession = await withTimeout(getCurrentSession(), START_NETWORK_BUDGET, TIMED_OUT)
        const netzSchweigt = currentSession === TIMED_OUT
        if (netzSchweigt) {
          console.warn('[Auth] Netz antwortet nicht — Start mit der gespeicherten Sitzung')
          currentSession = await readStoredSession()
        }
        setSession(currentSession)

        /*
          Schweigt das Netz schon bei der Sitzung, wird die Bestätigung gar
          nicht erst versucht: Sie liefe in dieselbe Frist, und der Start
          dauerte doppelt so lange — zehn Sekunden Logo statt fünf. Wir wissen
          an dieser Stelle bereits, dass nichts durchkommt.
        */
        if (currentSession?.user) {
          // Use cached session user immediately
          userIdRef.current = currentSession.user.id
          setUser(currentSession.user)
          syncSentryUser(currentSession.user)

          if (!netzSchweigt) {
            // Try to validate via network (non-blocking)
            try {
              const currentUser = await withTimeout(getCurrentUser(), START_NETWORK_BUDGET, null)
              if (currentUser) {
                userIdRef.current = currentUser.id
                setUser(currentUser)
                syncSentryUser(currentUser)
              }
            } catch {
              // Network unavailable — cached session user is still valid
              console.warn('[Auth] Could not validate user online, using cached session')
            }
          }
        } else if (!netzSchweigt) {
          // No cached session — try network as last resort
          try {
            const currentUser = await withTimeout(getCurrentUser(), START_NETWORK_BUDGET, null)
            if (currentUser) {
              userIdRef.current = currentUser.id
            }
            setUser(currentUser)
            syncSentryUser(currentUser)
          } catch {
            // Fully offline with no cached session
          }
        }
      } catch (error) {
        console.error('[Auth] Initialization error:', error)
      } finally {
        setLoading(false)
      }
    }

    initAuth()
  }, [])

  // Listen to auth state changes
  useEffect(() => {
    const handleAuthUser = async (newUser: AuthUser | null) => {
      // Hard `is_active` gate — runs on EVERY event that carries a user
      // (SIGNED_IN, TOKEN_REFRESHED, INITIAL_SESSION) BEFORE the dedup
      // skip, so an admin flipping `is_active` to false kicks the
      // client out within one refresh cycle (~50 min) instead of
      // sitting idle until the token actually expires. The profiles
      // select is the same row we already need for the onboarding
      // flag — folded into one query.
      const userChanged = (newUser?.id ?? null) !== userIdRef.current
      if (
        newUser &&
        supabase &&
        // Always check on a real user change (security); otherwise throttle so a
        // focus burst's repeated TOKEN_REFRESHED events don't re-hit profiles.
        (userChanged || isThrottleElapsed(Date.now(), lastActiveCheckAtRef.current, ACTIVE_CHECK_THROTTLE_MS))
      ) {
        lastActiveCheckAtRef.current = Date.now()
        try {
          const { data } = await supabase
            .from('profiles')
            .select('is_active, setup_flow_completed')
            .eq('id', newUser.id)
            .maybeSingle()
          if (data?.is_active === false) {
            // Deactivated → tear down the session; Dexie is intentionally
            // NOT wiped (per product decision — local data survives so
            // re-activation lets the user pick up where they left off).
            // The generic toast wording matches the invalid-credentials
            // path so the deactivation isn't observable.
            showErrorToast(t('setup.passwordInvalid'))
            await supabase.auth.signOut()
            return
          }
          // Existing onboarding-flag rehydration, kept in the same
          // query to avoid a second round-trip on every SIGNED_IN.
          if (
            data?.setup_flow_completed &&
            localStorage.getItem('onboarding_completed') !== 'true'
          ) {
            localStorage.setItem('onboarding_completed', 'true')
          }
        } catch (err) {
          console.warn('[Auth] Failed to load profile flags:', err)
        }
      }

      // Skip redundant updates — same user ID means no meaningful state change.
      // Prevents unnecessary re-render cascades that destabilize tab navigation.
      if (newUser?.id === userIdRef.current && newUser !== null) return

      // User-id-mismatch check — safety net for sessions that were not logged
      // out cleanly (silent session expire, crash mid-logout, user switching
      // accounts without explicit logout). When the newly-logged-in user
      // differs from the last-known user, wipe stale local state BEFORE
      // committing the new user to React state so the next render reads fresh flags.
      if (newUser) {
        const lastUserId = localStorage.getItem('lastUserId')
        if (lastUserId && lastUserId !== newUser.id) {
          console.warn('[Auth] User ID changed without clean logout — wiping stale state')
          await wipeUserLocalState()
        }
        localStorage.setItem('lastUserId', newUser.id)
        // Drop the cached profile so the sidebar/menu resolves the freshly
        // connected cloud identity instead of flashing the previous (offline)
        // one. On a real account switch wipeUserLocalState already did this; on
        // a first offline→cloud connect (lastUserId was null) it did not.
        clearUserDisplayProfileCache()
      }

      userIdRef.current = newUser?.id ?? null
      setUser(newUser)

      // Refresh session when user changes
      if (newUser) {
        getCurrentSession()
          .then(setSession)
          // A lock timeout here is not actionable — the cached session stays.
          // Without the catch it surfaces as an unhandled rejection, which in
          // dev hands the whole window to the dev-server error overlay.
          .catch(err => console.warn('[Auth] Session refresh failed:', err))
        // Note: OAuth callback handling is now done in OAuthCallbackPage component
        identifyUser(newUser.id, {
          platform: window.api ? 'electron' : 'web',
        })
        syncSentryUser(newUser)
      } else {
        setSession(null)
        resetAnalytics()
        syncSentryUser(null)
      }
    }

    // Supabase invokes this listener from INSIDE its Navigator LockManager lock
    // and AWAITS it (`GoTrueClient._notifyAllSubscribers`, called from
    // `_recoverAndRefresh` / `_callRefreshToken`). Anything awaited here keeps
    // the lock held: a hanging profiles round-trip (sleep/wake, VPN, offline)
    // made every concurrent auth call time out after 10 s with
    // `AbortError: signal is aborted without reason`. Returning synchronously
    // and continuing on the next macrotask releases the lock first. Order is
    // preserved — timers fire FIFO, so events stay in sequence.
    const unsubscribe = onAuthStateChange((newUser) => {
      setTimeout(() => { void handleAuthUser(newUser) }, 0)
    })

    return unsubscribe
    // Einmal abonnieren: Ein Sprachwechsel (t) soll den Auth-Listener nicht neu anmelden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Dedicated listener for PASSWORD_RECOVERY — Supabase's PKCE recovery flow
  // exchanges a `?code=` param for a session and fires a PASSWORD_RECOVERY event
  // on the client. No `access_token=` in the URL hash anymore, so the legacy
  // detector in index.html misses it. We catch it here and set the flag that
  // SetupRoute / SetupPage use to show ResetPasswordForm.
  useEffect(() => {
    if (!supabase) return
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        localStorage.setItem('passwordRecoveryMode', 'true')
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  // Auth actions
  const handleLogin = useCallback(async (params: LoginParams): Promise<LoginResult> => {
    const result = await loginUser(params)

    if (result.success && result.user && result.session) {
      userIdRef.current = result.user.id
      setUser(result.user)
      setSession(result.session)
    }

    return result
  }, [])

  const handleRegister = useCallback(async (params: RegisterParams): Promise<RegisterResult> => {
    const result = await registerUser(params)

    if (result.success && result.user) {
      userIdRef.current = result.user.id
      setUser(result.user)
    }

    return result
  }, [])

  const handleLogout = useCallback(async (): Promise<LogoutResult> => {
    // Capture BEFORE logoutUser()/clearing the ref. Only a real authenticated
    // cloud user reaches "Abmelden" (offline users get "Mit HappyCloud
    // verbinden"); guarding the wipe on this means no stray logout() on an
    // offline account — where signOut() is a no-op that still returns success —
    // can ever erase the user's only (local) copy of their data.
    const wasAuthenticated = userIdRef.current !== null
    const result = await logoutUser()

    if (result.success) {
      userIdRef.current = null
      setUser(null)
      setSession(null)

      if (wasAuthenticated) {
        // Real logout for a cloud user: the local data is a synced copy of the
        // cloud, so wiping it is safe and gives a clean slate. Clearing the
        // device-level `hasLoggedInBefore` hint sends the user back to the
        // WelcomeScreen (full reset) rather than straight to the login page.
        //
        // Electron only: there IS no WelcomeScreen on the web — `SetupRoute`
        // gates it on `isElectron`. Clearing the hint there achieved no reset,
        // it only downgraded the login view to the register one, against the
        // explicit note in `wipeLocalState.ts` that it must survive logout.
        await wipeUserLocalState()
        if (getPlatformAPI().platform === 'electron') {
          localStorage.removeItem('hasLoggedInBefore')
        }
      }
    }

    return result
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    user,
    session,
    loading,
    isAuthenticated: !!user,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
  }), [user, session, loading, handleLogin, handleRegister, handleLogout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
